/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  FormControlLabel,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useParams, Link } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { loadable } from '../utils/loadable';
import { regionState } from '../Regions/FemaRegions';
import { states } from '../utils/stateNameToAbbreviation';
import { ReactComponent as TempoLogo } from '../images/tempo_full_color_DKGray.svg';
import { ReactComponent as ImpactLogo } from '../images/IMPACT_DrkBlu_FULLCOLOR_ICON.svg';

const FEMA_EMAIL_PATTERN =
  /^[a-zA-Z0-9._%+-]+@(fema\.dhs\.gov|nltgis\.com|associates\.fema\.dhs\.gov)$/;

const SubscriptionManagePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  // A route with no token has nothing to load, so the page starts on the error
  // it would otherwise reach a render later.
  const [loading, setLoading] = useState(Boolean(token));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(token ? null : 'Invalid management link.');
  const [success, setSuccess] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [canopyAlerts, setCanopyAlerts] = useState(false);
  const [wowForecastThreats, setWowForecastThreats] = useState(false);
  const [wowIncidentAlerts, setWowIncidentAlerts] = useState(false);
  const [wildfireTaskerAlerts, setWildfireTaskerAlerts] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [canopyEf3OrHigher, setCanopyEf3OrHigher] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const femaRegionsJson = useAtomValue(loadable(regionState));

  useEffect(() => {
    if (!token) return;

    const loadSubscription = async () => {
      try {
        const response = await fetch(`/api/subscription/manage/${token}`);
        if (!response.ok) {
          setError('This link is invalid or has expired. Please request a new one.');
          return;
        }
        const data = await response.json();
        setEmail(data.email);
        if (data.subscription) {
          const types: string[] = data.subscription.subscriptionTypes ?? [];
          setCanopyAlerts(types.includes('canopy.observation'));
          setWowForecastThreats(types.includes('wow.forecast'));
          setWowIncidentAlerts(types.includes('wow.observation'));
          setWildfireTaskerAlerts(types.includes('wildfire.observation'));
          setSelectedRegions(data.subscription.regions ?? []);
          setSelectedStates(data.subscription.states ?? []);
          setCanopyEf3OrHigher(data.subscription.canopyEf3OrHigher ?? false);
        }
      } catch {
        setError('An error occurred. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    void loadSubscription();
  }, [token]);

  const handleSave = async (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSuccess(null);
    setError(null);

    const subscriptionTypes: string[] = [];
    if (wowForecastThreats) subscriptionTypes.push('wow.forecast');
    if (wowIncidentAlerts) subscriptionTypes.push('wow.observation');
    if (wildfireTaskerAlerts) subscriptionTypes.push('wildfire.observation');

    if (canopyAlerts) {
      if (FEMA_EMAIL_PATTERN.test(email)) {
        subscriptionTypes.push('canopy.observation');
      } else {
        setError('Canopy alerts require a valid FEMA email address.');
        return;
      }
    }

    if (subscriptionTypes.length === 0) {
      setError('Please select at least one alert type.');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`/api/subscription/manage/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionTypes,
          regions: selectedRegions,
          states: selectedStates,
          canopyEf3OrHigher,
        }),
      });
      if (response.ok) {
        setSuccess('Subscription saved successfully.');
      } else if (response.status === 401) {
        setError('This link has expired. Please request a new one from the subscriptions page.');
      } else if (response.status === 429) {
        setError('Too many requests. Please wait a while before trying again.');
      } else {
        setError('An error occurred while saving. Please try again.');
      }
    } catch {
      setError('An error occurred while saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribe = async () => {
    setConfirmDialogOpen(false);
    setSuccess(null);
    setError(null);
    setSaving(true);
    try {
      const response = await fetch(`/api/subscription/manage/${token}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        setDeleted(true);
      } else if (response.status === 401) {
        setError('This link has expired. Please request a new one from the subscriptions page.');
      } else if (response.status === 429) {
        setError('Too many requests. Please wait a while before trying again.');
      } else {
        setError('An error occurred while unsubscribing. Please try again.');
      }
    } catch {
      setError('An error occurred while unsubscribing. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error && !email) {
    return (
      <Grid
        container
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          mb: 10,
          overflow: 'auto',
        }}
      >
        <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
          <Paper elevation={3} style={{ padding: '24px' }}>
            <TempoLogo width="240" style={{ paddingBottom: '20px' }} />
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button
              component={Link}
              to="/subscriptions"
              variant="contained"
              color="primary"
              fullWidth
            >
              Request a New Link
            </Button>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  if (deleted) {
    return (
      <Grid
        container
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          mb: 10,
          overflow: 'auto',
        }}
      >
        <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
          <Paper elevation={3} style={{ padding: '24px' }}>
            <TempoLogo width="240" style={{ paddingBottom: '20px' }} />
            <Alert severity="success" sx={{ mb: 2 }}>
              You have been unsubscribed from TEMPO alerts.
            </Alert>
            <Button
              component={Link}
              to="/subscriptions"
              variant="contained"
              color="primary"
              fullWidth
            >
              Back to Subscriptions
            </Button>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        mb: 10,
        overflow: 'auto',
      }}
    >
      <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
        <Paper elevation={3} style={{ padding: '24px' }}>
          <TempoLogo width="240" style={{ paddingBottom: '20px' }} />
          <Typography variant="h4" align="center">
            Manage TEMPO Alerts
          </Typography>

          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSave}>
            <FormControl sx={{ width: '100%' }}>
              <TextField
                label="Email Address"
                variant="outlined"
                fullWidth
                margin="normal"
                type="email"
                value={email}
                slotProps={{ input: { readOnly: true } }}
                disabled
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={canopyAlerts}
                    onChange={e => {
                      setCanopyAlerts(e.target.checked);
                      if (!e.target.checked) {
                        setCanopyEf3OrHigher(false);
                      }
                    }}
                    color="primary"
                    disabled={!FEMA_EMAIL_PATTERN.test(email)}
                  />
                }
                label="TEMPO Tornado Alerts (Requires FEMA Email)"
              />
              <Typography variant="body2" sx={{ mb: 1 }}>
                Provides details on tornado location, structures exposed, and approximate maximum EF
                rank for new or updated tornado swaths every 15 minutes. Tornado swaths are provided
                by Canopy Weather. <b>FEMA internal users only.</b>
              </Typography>
              <FormControlLabel
                sx={{ ml: 3 }}
                control={
                  <Checkbox
                    checked={canopyEf3OrHigher}
                    onChange={e => setCanopyEf3OrHigher(e.target.checked)}
                    color="primary"
                    disabled={!canopyAlerts}
                  />
                }
                label="Only EF3 or higher"
              />
              <Typography variant="body2" sx={{ mb: 1, ml: 3 }}>
                Only receive Tornado Alerts for swaths with an estimated EF scale ranking of EF3 or
                higher.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={wildfireTaskerAlerts}
                    onChange={e => setWildfireTaskerAlerts(e.target.checked)}
                    color="primary"
                  />
                }
                label="TEMPO Wildfire Tasker"
              />
              <Typography variant="body2" sx={{ mb: 1 }}>
                Provides geospatial data of the extents of wildfires with the largest numbers of
                exposed structures. Fire perimeters are provided by the National Interagency Fire
                Center (NIFC). Emails are designed for tasking of imagery collection. For users
                instead seeking summary alerts of wildfire threats or incidents, we recommend the
                WOW Forecast or Incident alerts above.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={wowForecastThreats}
                    onChange={e => setWowForecastThreats(e.target.checked)}
                    color="primary"
                  />
                }
                label="WOW Forecast Alerts"
              />
              <Typography variant="body2" sx={{ mb: 1 }}>
                Daily email update summarizing the worst threats from national forecasts related to
                tornado outlook, flooding, winter weather, and wildfires. Summary includes
                locations, threat severities, and potential impacts. Available to all users.
              </Typography>{' '}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={wowIncidentAlerts}
                    onChange={e => setWowIncidentAlerts(e.target.checked)}
                    color="primary"
                  />
                }
                label="WOW Incident Alerts"
              />
            </FormControl>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Alerts of the most severe and impactful earthquakes, wildfires, and tornadoes, updated
              every 15 minutes. Summary includes locations, severities, and exposed structures, with
              links to additional data. Available to all users.
            </Typography>
            <Typography variant="h5" align="center" sx={{ mt: 4 }}>
              Optionally filter alerts by State or Region
            </Typography>

            <FormControl sx={{ p: 1, width: '100%' }}>
              <Typography variant="h6" align="center">
                FEMA Regions
              </Typography>
              <Typography variant="body2" align="center" style={{ marginBottom: '16px' }}>
                Select the FEMA regions you would like to receive alerts for.
              </Typography>
              <Select
                multiple
                value={selectedRegions}
                onChange={event => setSelectedRegions(event.target.value as string[])}
                renderValue={selected => selected?.join(', ')}
              >
                {femaRegionsJson.state === 'hasData' &&
                  [...femaRegionsJson.data.features]
                    .sort((a, b) => a.properties.region - b.properties.region)
                    .map(feature => (
                      <MenuItem key={feature.properties.region} value={feature.properties.region}>
                        {`Region ${feature.properties.region}`}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
            <FormControl sx={{ p: 1, position: 'relative', width: '100%' }}>
              <Typography variant="h6" align="center">
                States
              </Typography>
              <Typography variant="body2" align="center" style={{ marginBottom: '16px' }}>
                Select the states you would like to receive alerts for.
              </Typography>
              <Select
                multiple
                value={selectedStates}
                onChange={event => setSelectedStates(event.target.value as string[])}
                renderValue={selected => selected.join(', ')}
              >
                {Object.entries(states).map(([state, stateAbbr]) => {
                  return (
                    <MenuItem key={state} value={stateAbbr}>
                      {state}, {stateAbbr}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Button variant="contained" color="primary" type="submit" fullWidth disabled={saving}>
              {saving ? 'Saving...' : 'Save Subscription'}
            </Button>
          </form>
          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            color="error"
            fullWidth
            disabled={saving}
            onClick={() => setConfirmDialogOpen(true)}
          >
            {saving ? 'Processing...' : 'Unsubscribe'}
          </Button>
          <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
            <DialogTitle>Confirm Unsubscribe</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to unsubscribe from all TEMPO alerts?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUnsubscribe} color="error" autoFocus>
                Unsubscribe
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 2,
            textAlign: 'center',
            backgroundColor: 'transparent',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              mb: 2,
              fontSize: '0.875rem',
            }}
          >
            Powered by{' '}
            <a
              href="https://newlighttechnologies.com/impact"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#666',
                textDecoration: 'underline',
              }}
            >
              New Light Technologies (NLT) IMPACT Platform
            </a>
          </Typography>
          <a
            href="https://newlighttechnologies.com/impact"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <ImpactLogo
              width="200px"
              style={{
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
              }}
              onMouseOver={e => (e.currentTarget.style.opacity = '0.8')}
              onMouseOut={e => (e.currentTarget.style.opacity = '1')}
            />
          </a>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default SubscriptionManagePage;
